import { useCallback, useEffect, useRef, useState } from 'react';
import type { CardSavePhase } from '../components/tenant/CardSaveBadge';

/** Closing cards that save immediately (upload, delete, e-transfer ack). */
export type ClosingSaveCardKey =
  | 'utilities'
  | 'housemate'
  | 'pad'
  | 'voidCheque'
  | 'eTransfer';

const CARD_KEYS: ClosingSaveCardKey[] = [
  'utilities',
  'housemate',
  'pad',
  'voidCheque',
  'eTransfer',
];

const IDLE_PHASES = Object.fromEntries(CARD_KEYS.map((key) => [key, 'idle'])) as Record<
  ClosingSaveCardKey,
  CardSavePhase
>;

/** Match CardSaveBadge saved pill timing so the next save can show a spinner again. */
const SAVED_RESET_MS = 3200;

export function useClosingCardSavePhase() {
  const [phases, setPhases] = useState<Record<ClosingSaveCardKey, CardSavePhase>>({ ...IDLE_PHASES });
  const timersRef = useRef<Partial<Record<ClosingSaveCardKey, number>>>({});

  const clearSavedTimer = useCallback((key: ClosingSaveCardKey) => {
    const existing = timersRef.current[key];
    if (existing != null) {
      window.clearTimeout(existing);
      delete timersRef.current[key];
    }
  }, []);

  const scheduleIdle = useCallback(
    (key: ClosingSaveCardKey) => {
      clearSavedTimer(key);
      timersRef.current[key] = window.setTimeout(() => {
        delete timersRef.current[key];
        setPhases((current) => (current[key] === 'saved' ? { ...current, [key]: 'idle' } : current));
      }, SAVED_RESET_MS);
    },
    [clearSavedTimer],
  );

  useEffect(
    () => () => {
      for (const key of CARD_KEYS) clearSavedTimer(key);
    },
    [clearSavedTimer],
  );

  const markSaving = useCallback((key: ClosingSaveCardKey) => {
    clearSavedTimer(key);
    setPhases((current) => ({ ...current, [key]: 'saving' }));
  }, [clearSavedTimer]);

  const markSaved = useCallback(
    (key: ClosingSaveCardKey) => {
      setPhases((current) => ({ ...current, [key]: 'saved' }));
      scheduleIdle(key);
    },
    [scheduleIdle],
  );

  const markIdle = useCallback(
    (key: ClosingSaveCardKey) => {
      clearSavedTimer(key);
      setPhases((current) => ({ ...current, [key]: 'idle' }));
    },
    [clearSavedTimer],
  );

  return { phases, markSaving, markSaved, markIdle };
}
